import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Analytics, logEvent } from '../core/firebase/analytics';
import {
  ApplicationKind,
  Ga4Event,
  LegacyGa4Event,
  applicationSentParams,
  jobEventParams,
} from '../core/ga4-events';
import { Observable, forkJoin, map, of, switchMap } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Job } from '../types/job';
import { BucketFile } from '../types/core';
import { Application, ApplicationRequest } from '../types/application';
import { JobService } from './job.service';
import { SchoolService } from './school.service';
import { School } from '../types/school';
import { initiativeJob } from '../core/initiative-application';
import { FileApiService } from './file-api.service';
import { fileRefToBucketFile } from '../core/file-utils';
import {
  ClaimApplicationsRes,
  CreateInitiativeApplicationReq,
  ApplicantDetailsReq,
  CreateJobApplicationReq,
  GuestApplicationPrefill,
  IdRes,
  ReviewApplicationReq,
  StatusRes,
  UpdateApplicationArchivedReq,
  UpdateCoverLetterReq,
  UpdateUserCoverLetterReq,
} from '../types/rest-api';

export interface PublicApplyBody extends ApplicantDetailsReq {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  cover_letter: string;
  cv: File;
  tandem_cv?: File | null;
  diploma_certificates?: File[];
  reference_letters?: File[];
}

/** Multipart form fields for the optional applicant details; empty ones are left out. */
export function appendApplicantDetails(
  form: FormData,
  details: ApplicantDetailsReq
): void {
  const fields: [string, string | null | undefined][] = [
    ['salutation', details.salutation],
    ['birth_date', details.birth_date],
    ['street', details.street],
    ['postal_code', details.postal_code],
    ['city', details.city],
    ['qualification', details.qualification],
  ];
  for (const [key, value] of fields) {
    if (value) form.append(key, value);
  }
  if (details.confirmations_accepted) {
    form.append('confirmations_accepted', 'true');
  }
}

/** Recipient of an initiative application: exactly one of both. */
export interface InitiativeRecipient {
  school_id: string | null;
  school_authority_id: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ApplicationService {
  private readonly apiBase = environment.apiBaseUrl;
  private http = inject(HttpClient);
  private analytics = inject(Analytics);
  private jobService = inject(JobService);
  private schoolService = inject(SchoolService);
  private fileApi = inject(FileApiService);

  // --- Application CRUD ---

  /** GET /v1/job-applications – list all applications for the current user */
  getAll(isArchived = false): Observable<Application[]> {
    return this.http
      .get<any[]>(`${this.apiBase}/v1/job-applications`, {
        params: { is_archived: isArchived },
      })
      .pipe(
        map(rows => rows.map(row => this.mapSummaryRowToApplication(row))),
        switchMap(apps => this.hydrateApplicationJobUrls(apps))
      );
  }

  /**
   * Get applications for the current user, optionally filtered by archived state.
   * Replaces the old Firestore-based getMy() method.
   */
  getMy(archived = false): Observable<Application[]> {
    return this.getAll(archived);
  }

  /**
   * Check whether the current user has already applied to a given job posting.
   * Uses getAll() and filters client-side by jobPostingId.
   */
  hasAlreadyApplied(jobId: string): Observable<boolean> {
    return this.getAll().pipe(
      map(applications =>
        applications.some(
          (a: any) => a.job_posting_id === jobId || a.job?.id === jobId
        )
      )
    );
  }

  /** GET /v1/job-postings/{jobPostingId}/applications – get all applications for a job posting */
  getByJobId(jobId: string, _schoolId?: string): Observable<Application[]> {
    return this.http
      .get<any[]>(`${this.apiBase}/v1/job-postings/${jobId}/applications`)
      .pipe(
        map(rows => rows.map(row => this.mapSummaryRowToApplication(row))),
        switchMap(apps => this.hydrateApplicationContacts(apps)),
        switchMap(apps => this.hydrateApplicationJobUrls(apps))
      );
  }

  /**
   * The list endpoint (`JobApplicationSummary`) does not include `phone` / `email`.
   * The HR applicants overview shows phone, so fetch each application's detail in
   * parallel and merge the contact fields into the row objects.
   */
  private hydrateApplicationContacts(
    apps: Application[]
  ): Observable<Application[]> {
    if (apps.length === 0) {
      return of(apps);
    }
    const details$ = apps.map(a =>
      this.http
        .get<any>(`${this.apiBase}/v1/job-applications/${a.id}`)
        .pipe(catchError(() => of(null)))
    );
    return forkJoin(details$).pipe(
      map(details => {
        details.forEach((d, i) => {
          if (!d) return;
          apps[i].phone = d.phone ?? apps[i].phone;
          apps[i].email = d.email ?? apps[i].email;
        });
        return apps;
      })
    );
  }

  /**
   * Hydrate URLs for both the application's job assets (school avatar/wall/media)
   * and the applicant's own avatar (HR-side rows render `application.avatar`).
   * Resolvers mutate the referenced objects in place.
   */
  private hydrateApplicationJobUrls(
    apps: Application[]
  ): Observable<Application[]> {
    const jobs = apps.map(a => a.job).filter((j): j is Job => !!j);
    // Only resolve avatars whose `path` was not pre-populated by an inline FileRef.
    const applicantsNeedingAvatar = apps.filter(
      a => a.avatar?.filename && !a.avatar.path
    );
    const applicantAvatarIds = applicantsNeedingAvatar.map(
      a => a.avatar!.filename!
    );

    if (jobs.length === 0 && applicantAvatarIds.length === 0) {
      return of(apps);
    }

    const jobs$ =
      jobs.length > 0
        ? this.jobService.resolveJobFileUrlsPublic(jobs)
        : of(jobs);
    const avatars$ =
      applicantAvatarIds.length > 0
        ? this.fileApi.resolveFileUrls(applicantAvatarIds)
        : of(new Map<string, string>());

    return forkJoin([jobs$, avatars$]).pipe(
      map(([, urlMap]) => {
        for (const a of applicantsNeedingAvatar) {
          const url = urlMap.get(a.avatar!.filename!);
          if (url) {
            a.avatar = { ...a.avatar!, path: url };
          }
        }
        return apps;
      })
    );
  }

  /**
   * Reject an application via PUT /v1/job-postings/{jobPostingId}/applications/{applicationId}/review.
   * The jobPostingId is derived from the application's job.id field.
   */
  reject(
    applicationId: string,
    text: string,
    jobPostingId?: string
  ): Observable<StatusRes> {
    const resolvedJobPostingId = jobPostingId || applicationId;
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${resolvedJobPostingId}/applications/${applicationId}/review`,
      {
        is_accepted: false,
        rejection_message: text,
      } satisfies ReviewApplicationReq
    );
  }

  /** GET /v1/job-applications/{id} – get application detail */
  getById(id: string): Observable<Application> {
    return this.http
      .get<any>(`${this.apiBase}/v1/job-applications/${id}`)
      .pipe(
        switchMap(detail =>
          (detail.job_posting_id
            ? this.jobService.getById(String(detail.job_posting_id))
            : this.getInitiativeJob(detail)
          ).pipe(
            map(job => this.mapJobApplicationDetailToApplication(detail, job))
          )
        )
      );
  }

  /** Initiative applications have no posting: show the school they went to. */
  private getInitiativeJob(detail: {
    school_id?: string | null;
    school_authority_id?: string | null;
  }): Observable<Job> {
    const schoolId = detail.school_id ?? null;
    const authorityId = detail.school_authority_id ?? null;
    const ownerId = schoolId ?? authorityId;
    const owner$ = ownerId
      ? this.schoolService
          .getById(ownerId)
          .pipe(catchError(() => of(null as School | null)))
      : of(null as School | null);
    return owner$.pipe(
      map(owner => initiativeJob(schoolId, authorityId, owner))
    );
  }

  /** POST /v1/job-applications/job-postings/{jobPostingId} – create a new application for a job posting */
  create(
    jobPostingId: string,
    body: CreateJobApplicationReq
  ): Observable<IdRes> {
    // Log on success only — firing before the request counted failed/abandoned
    // attempts as real creations (GA over-reported vs. rows actually created).
    return this.http
      .post<IdRes>(
        `${this.apiBase}/v1/job-applications/job-postings/${jobPostingId}`,
        body
      )
      .pipe(
        tap(() =>
          logEvent(this.analytics, 'application_create', { jobPostingId })
        )
      );
  }

  /**
   * POST /v1/job-applications/public/job-postings/{jobPostingId} – submit a public job
   * application (no account required) in a single multipart request.
   * `job` only enriches the GA4 event with job title and school; `language` sets the
   * language of the confirmation mail (backend default: German).
   */
  publicApply(
    jobPostingId: string,
    body: PublicApplyBody,
    job?: Job,
    language?: string
  ): Observable<StatusRes> {
    const form = new FormData();
    form.append('first_name', body.first_name);
    form.append('last_name', body.last_name);
    form.append('email', body.email);
    form.append('phone', body.phone);
    form.append('cover_letter', body.cover_letter);
    form.append('cv', body.cv);
    if (body.tandem_cv) {
      form.append('tandem_cv', body.tandem_cv);
    }
    for (const f of body.diploma_certificates ?? []) {
      form.append('diploma_certificates', f);
    }
    for (const f of body.reference_letters ?? []) {
      form.append('reference_letters', f);
    }
    appendApplicantDetails(form, body);
    if (language) {
      form.append('language', language);
    }
    return this.http
      .post<StatusRes>(
        `${this.apiBase}/v1/job-applications/public/job-postings/${jobPostingId}`,
        form
      )
      .pipe(
        tap(() => {
          logEvent(this.analytics, LegacyGa4Event.applicationPublicApply, {
            jobPostingId,
            ...jobEventParams(job),
          });
          this.logApplicationSent('gast', job ?? { id: jobPostingId });
        })
      );
  }

  /**
   * Apply to a job.
   * Accepts either:
   *  - a string (new signature): the application ID to submit
   *  - an ApplicationRequest object (legacy signature): extracts job.id and submits
   * `job` only enriches the GA4 event with job title and school.
   */
  apply(
    idOrRequest: string | ApplicationRequest,
    job?: Job
  ): Observable<StatusRes> {
    if (typeof idOrRequest === 'string') {
      return this.http
        .post<StatusRes>(
          `${this.apiBase}/v1/job-applications/${idOrRequest}/apply`,
          {}
        )
        .pipe(
          tap(() => {
            logEvent(this.analytics, LegacyGa4Event.applicationApply, {
              applicationId: idOrRequest,
              ...jobEventParams(job),
            });
            this.logApplicationSent('konto', job);
          })
        );
    }
    // Legacy object signature: use job.id as the job posting id and submit via create+apply
    const jobId = idOrRequest.job.id;
    return this.http
      .post<StatusRes>(
        `${this.apiBase}/v1/job-applications/job-postings/${jobId}/apply`,
        {
          email: idOrRequest.email,
          phone: idOrRequest.phone,
          cover_letter: idOrRequest.message,
        } as CreateJobApplicationReq
      )
      .pipe(
        tap(() => {
          logEvent(this.analytics, LegacyGa4Event.applicationApply, {
            jobId,
            ...jobEventParams(job ?? idOrRequest.job),
          });
          this.logApplicationSent('konto', job ?? idOrRequest.job);
        })
      );
  }

  /** `bewerbung_gesendet`: one event per sent application, guest or account. */
  private logApplicationSent(
    kind: ApplicationKind,
    job: Partial<Job> | undefined
  ): void {
    logEvent(
      this.analytics,
      Ga4Event.applicationSent,
      applicationSentParams(kind, job)
    );
  }

  /** POST /v1/job-applications/{id}/withdraw – withdraw an application */
  withdraw(id: string): Observable<StatusRes> {
    return this.http.post<StatusRes>(
      `${this.apiBase}/v1/job-applications/${id}/withdraw`,
      {}
    );
  }

  /** PUT /v1/job-applications/{id}/archive – archive or unarchive an application */
  archive(id: string, isArchived = true): Observable<StatusRes> {
    return this.http
      .put<StatusRes>(`${this.apiBase}/v1/job-applications/${id}/archive`, {
        is_archived: isArchived,
      } satisfies UpdateApplicationArchivedReq)
      .pipe(
        tap(() => {
          // Only track real archiving. Unarchiving (e.g. right after applying in
          // job-apply) would otherwise show up as a bogus `application_archive`.
          if (isArchived) {
            logEvent(this.analytics, 'application_archive', {
              applicationId: id,
            });
          }
        })
      );
  }

  // --- Application documents ---

  /** PUT /v1/job-applications/{id}/cover-letter – update cover letter for an application */
  updateCoverLetter(
    id: string,
    body: UpdateCoverLetterReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-applications/${id}/cover-letter`,
      body
    );
  }

  /** PUT /v1/job-applications/{id}/cv – upload CV for an application */
  uploadApplicationCv(id: string, file: File): Observable<StatusRes> {
    const form = new FormData();
    form.append('file', file);
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-applications/${id}/cv`,
      form
    );
  }

  /** PUT /v1/job-applications/{id}/tandem-cv – upload tandem CV for an application */
  uploadApplicationTandemCv(id: string, file: File): Observable<StatusRes> {
    const form = new FormData();
    form.append('file', file);
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-applications/${id}/tandem-cv`,
      form
    );
  }

  /** POST /v1/job-applications/{id}/diploma-certificates – upload a diploma certificate for an application */
  uploadApplicationDiplomaCertificate(
    id: string,
    file: File
  ): Observable<StatusRes> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<StatusRes>(
      `${this.apiBase}/v1/job-applications/${id}/diploma-certificates`,
      form
    );
  }

  /** DELETE /v1/job-applications/{id}/diploma-certificates/{docId} – delete a diploma certificate from an application */
  deleteApplicationDiplomaCertificate(
    id: string,
    docId: string
  ): Observable<StatusRes> {
    return this.http.delete<StatusRes>(
      `${this.apiBase}/v1/job-applications/${id}/diploma-certificates/${docId}`
    );
  }

  /** POST /v1/job-applications/{id}/reference-letters – upload a reference letter for an application */
  uploadApplicationReferenceLetter(
    id: string,
    file: File
  ): Observable<StatusRes> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<StatusRes>(
      `${this.apiBase}/v1/job-applications/${id}/reference-letters`,
      form
    );
  }

  /** DELETE /v1/job-applications/{id}/reference-letters/{docId} – delete a reference letter from an application */
  deleteApplicationReferenceLetter(
    id: string,
    docId: string
  ): Observable<StatusRes> {
    return this.http.delete<StatusRes>(
      `${this.apiBase}/v1/job-applications/${id}/reference-letters/${docId}`
    );
  }

  /** POST /v1/job-applications/initiative – initiative application with library files */
  createInitiative(body: CreateInitiativeApplicationReq): Observable<IdRes> {
    return this.http.post<IdRes>(
      `${this.apiBase}/v1/job-applications/initiative`,
      body
    );
  }

  /** POST /v1/job-applications/public/initiative – initiative application without an account */
  publicInitiativeApply(
    recipient: InitiativeRecipient,
    body: PublicApplyBody,
    language: string
  ): Observable<StatusRes> {
    const form = new FormData();
    form.append('first_name', body.first_name);
    form.append('last_name', body.last_name);
    form.append('email', body.email);
    form.append('phone', body.phone);
    form.append('cover_letter', body.cover_letter);
    form.append('cv', body.cv);
    if (body.tandem_cv) {
      form.append('tandem_cv', body.tandem_cv);
    }
    for (const f of body.diploma_certificates ?? []) {
      form.append('diploma_certificates', f);
    }
    for (const f of body.reference_letters ?? []) {
      form.append('reference_letters', f);
    }
    appendApplicantDetails(form, body);
    if (recipient.school_id) {
      form.append('school_id', recipient.school_id);
    }
    if (recipient.school_authority_id) {
      form.append('school_authority_id', recipient.school_authority_id);
    }
    form.append('language', language);
    return this.http.post<StatusRes>(
      `${this.apiBase}/v1/job-applications/public/initiative`,
      form
    );
  }

  // --- Guest becomes account ---

  /** GET /v1/job-applications/guest-prefill – personal data from the own guest applications */
  getGuestPrefill(): Observable<GuestApplicationPrefill> {
    return this.http.get<GuestApplicationPrefill>(
      `${this.apiBase}/v1/job-applications/guest-prefill`
    );
  }

  /** POST /v1/job-applications/claim – moves the own guest applications to the account */
  claimGuestApplications(): Observable<ClaimApplicationsRes> {
    return this.http.post<ClaimApplicationsRes>(
      `${this.apiBase}/v1/job-applications/claim`,
      {}
    );
  }

  // --- User documents library ---

  /** GET /v1/job-applications/documents – get the user's document library */
  getUserDocuments(): Observable<unknown> {
    return this.http.get(`${this.apiBase}/v1/job-applications/documents`);
  }

  /** PUT /v1/job-applications/documents/cover-letter – update user's default cover letter */
  updateUserCoverLetter(body: UpdateUserCoverLetterReq): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-applications/documents/cover-letter`,
      body
    );
  }

  /** PUT /v1/job-applications/documents/cv – upload user's CV to the document library */
  uploadUserCv(file: File): Observable<StatusRes> {
    const form = new FormData();
    form.append('file', file);
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-applications/documents/cv`,
      form
    );
  }

  /** DELETE /v1/job-applications/documents/cv – delete user's CV from the document library */
  deleteUserCv(): Observable<StatusRes> {
    return this.http.delete<StatusRes>(
      `${this.apiBase}/v1/job-applications/documents/cv`
    );
  }

  /** PUT /v1/job-applications/documents/tandem-cv – upload user's tandem CV to the document library */
  uploadUserTandemCv(file: File): Observable<StatusRes> {
    const form = new FormData();
    form.append('file', file);
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-applications/documents/tandem-cv`,
      form
    );
  }

  /** DELETE /v1/job-applications/documents/tandem-cv – delete user's tandem CV from the document library */
  deleteUserTandemCv(): Observable<StatusRes> {
    return this.http.delete<StatusRes>(
      `${this.apiBase}/v1/job-applications/documents/tandem-cv`
    );
  }

  /** POST /v1/job-applications/documents/diploma-certificates – upload a diploma certificate to the document library */
  uploadUserDiplomaCertificate(file: File): Observable<IdRes> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<IdRes>(
      `${this.apiBase}/v1/job-applications/documents/diploma-certificates`,
      form
    );
  }

  /** DELETE /v1/job-applications/documents/diploma-certificates/{documentFileId} – delete a diploma certificate from the document library */
  deleteUserDiplomaCertificate(documentFileId: string): Observable<StatusRes> {
    return this.http.delete<StatusRes>(
      `${this.apiBase}/v1/job-applications/documents/diploma-certificates/${documentFileId}`
    );
  }

  /** POST /v1/job-applications/documents/reference-letters – upload a reference letter to the document library */
  uploadUserReferenceLetter(file: File): Observable<IdRes> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<IdRes>(
      `${this.apiBase}/v1/job-applications/documents/reference-letters`,
      form
    );
  }

  /** DELETE /v1/job-applications/documents/reference-letters/{documentFileId} – delete a reference letter from the document library */
  deleteUserReferenceLetter(documentFileId: string): Observable<StatusRes> {
    return this.http.delete<StatusRes>(
      `${this.apiBase}/v1/job-applications/documents/reference-letters/${documentFileId}`
    );
  }

  /**
   * List endpoints return OpenAPI `JobApplicationSummary` (no nested `job`).
   * UI expects {@link Application} with `job` populated for cards.
   */
  private mapSummaryRowToApplication(row: any): Application {
    const r = row as any;
    const job = this.jobService.mapJobApplicationSummaryToJob(row);
    const user = r.user || {};
    // Public applicants have no `user` — the API exposes their name on
    // top-level `applicant_first_name` / `applicant_last_name` fields.
    const firstName = r.applicant_first_name ?? user.first_name ?? '';
    const lastName = r.applicant_last_name ?? user.last_name ?? '';
    const fullName = `${firstName} ${lastName}`.trim();
    return {
      id: r.id,
      job,
      userId: user.id,
      name: fullName || undefined,
      avatar: fileRefToBucketFile(
        user.profile_picture,
        user.profile_picture_id
      ),
      email: user.email ?? '',
      phone: r.phone,
      status: r.status,
      archived: r.is_archived ?? false,
      isDeleted: false,
      isAnonymousUser: false,
      cv: null,
      createdOn: r.applied_at,
      modifiedOn: r.applied_at,
    } as Application;
  }

  private mapJobApplicationDetailToApplication(
    detail: any,
    job: Job
  ): Application {
    const d = detail as any;
    const docs = d.documents || {};
    const fileToBucket = (
      f: {
        file_id?: string;
        content_type?: string;
        file_name?: string;
      } | null
    ): BucketFile | null =>
      f?.file_id
        ? {
            path: '',
            filename: f.file_id,
            mimetype: f.content_type,
            description: f.file_name,
            url: f.file_id,
          }
        : null;

    const diplomaCerts = (docs.diploma_certificates || [])
      .map((f: any) => fileToBucket(f))
      .filter(Boolean) as BucketFile[];

    // Public applicants have no `user` — the API exposes their name on
    // top-level `applicant_first_name` / `applicant_last_name` fields.
    const firstName = d.applicant_first_name ?? d.user?.first_name ?? '';
    const lastName = d.applicant_last_name ?? d.user?.last_name ?? '';

    return {
      id: d.id,
      job,
      userId: d.user?.id,
      email: d.email,
      phone: d.phone,
      message: docs.cover_letter ?? undefined,
      status: d.status,
      archived: d.is_archived ?? false,
      isDeleted: false,
      isAnonymousUser: false,
      name: `${firstName} ${lastName}`.trim() || undefined,
      title: '',
      cv: fileToBucket(docs.cv),
      jobSharingCV: fileToBucket(docs.tandem_cv),
      diplomas: [],
      certificates: [],
      qualifications: diplomaCerts,
      additionalDocs: (docs.reference_letters || [])
        .map((f: any) => fileToBucket(f))
        .filter(Boolean) as BucketFile[],
      createdOn: d.applied_at,
      modifiedOn: d.status_changed_at,
    } as Application;
  }
}
